import { forwardRef } from 'react';
import { Gift } from 'lucide-react';
import styles from './ShopPromoPoster.module.css';
import ultraPanda from './games/ultra-panda.jpg';
import fireKirin from './games/fire-kirin.jpg';
import gameVault from './games/game-vault.jpg';
import orionStars from './games/orion-stars.jpeg';
import riverpay from './games/riverpay.jpg';
import vblink from './games/vblink.jpg';
import vegasX from './games/vegas-x.jpg';

/** A4 portrait at 96dpi: the size the poster is laid out, previewed and exported at. */
export const POSTER_WIDTH = 794;
export const POSTER_HEIGHT = 1123;
/** Page colour behind the poster, also used to fill the exported PNG. */
export const POSTER_BACKGROUND = '#0B0C11';

/**
 * Game logos for the cluster, front to back: the first is the big one on top (Ultra Panda), then
 * left, right, far left, far right, and the two small ones at the back. Logos only, no names.
 */
const CLUSTER_LOGOS = [ultraPanda, fireKirin, gameVault, orionStars, riverpay, vblink, vegasX];

/** The ways to pay the player app lists under "Pay your way". */
const PAYMENT_METHODS = ['Crypto', 'Cash App', 'Chime', 'Apple Pay', 'Google Pay'];

const STEPS_WITH_BONUS = ['Scan the code', 'Sign up', 'Get your bonus'];
const STEPS_WITHOUT_BONUS = ['Scan the code', 'Sign up', 'Pick your game'];

export type ShopPromoPosterProps = {
  /** Full kiosk URL the QR code points to, e.g. https://myshop.example.com */
  kioskUrl: string;
  qrDataUrl: string | null;
  /**
   * Show the welcome bonus (the bar across the top, the offer beside the QR code, the last step).
   * Off, the same frame says how to start instead and the game cluster takes the bar's space.
   */
  showBonus?: boolean;
  /** The new-player offer, e.g. "$10 FREE" */
  bonusCta?: string;
};

/** Widest the offer can be beside the QR code, and how wide one of its characters is per px of type. */
const OFFER_WIDTH = 336;
const OFFER_CHAR_EM = 0.58;
const OFFER_MAX_SIZE = 60;

/** Type size that keeps the offer on one line: 60px for "$10 FREE", smaller for a longer amount. */
function offerFontSize(offer: string): number {
  return Math.min(OFFER_MAX_SIZE, Math.floor(OFFER_WIDTH / (Math.max(offer.length, 1) * OFFER_CHAR_EM)));
}

/** The address as a person would type it: no protocol, no trailing slash. */
function readableUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

/**
 * Vault Play promo poster for a shop to print or share (KIOSK-460). It wears the player app's
 * theme (v2 "Dusk": near-black page, flat hairline cards, the blue-violet action colour, gold for
 * money to the player, Plus Jakarta Sans and IBM Plex Mono) and its game cluster (the pile of
 * tilted game logos from the app's first screen). Built to be read from across a room: a few big
 * lines, one large QR code, the address in plain text.
 */
const ShopPromoPoster = forwardRef<HTMLDivElement, ShopPromoPosterProps>(function ShopPromoPoster(
  { kioskUrl, qrDataUrl, showBonus = true, bonusCta = '$10 FREE' },
  ref
) {
  const steps = showBonus ? STEPS_WITH_BONUS : STEPS_WITHOUT_BONUS;

  return (
    <div ref={ref} className={`${styles.surface}${showBonus ? '' : ` ${styles.surfaceNoBonus}`}`}>
      {showBonus ? (
        <div className={styles.bonusBar} data-testid="poster-bonus-bar">
          <Gift className={styles.bonusBarIcon} aria-hidden />
          <span className={styles.bonusBarText}>New Player bonus</span>
          <span className={styles.bonusBarAmount}>{bonusCta}</span>
        </div>
      ) : null}

      <div className={styles.content}>
        <h1 className={styles.brand}>
          Vault <span className={styles.brandAccent}>Play</span>
        </h1>

        <div className={styles.cluster} aria-hidden data-testid="poster-game-cluster">
          <div className={styles.clusterGlow} />
          {CLUSTER_LOGOS.map((logo, index) => (
            <div key={logo} className={`${styles.clusterSlot} ${styles[`clusterSlot${index + 1}`]}`}>
              <img src={logo} alt="" />
            </div>
          ))}
        </div>

        <p className={styles.headline}>One wallet. All your games.</p>
        <p className={styles.sub}>Pay from your phone. No cash needed.</p>

        <section className={styles.payCard}>
          <p className={styles.cardLabel}>Pay your way</p>
          <ul className={styles.payList}>
            {PAYMENT_METHODS.map((method) => (
              <li key={method} className={styles.payChip}>
                {method}
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.scanCard}>
          <div className={styles.qrTile}>
            {qrDataUrl ? (
              <img className={styles.qrImg} src={qrDataUrl} alt="" />
            ) : (
              <div className={styles.qrPlaceholder} aria-hidden />
            )}
          </div>
          <div className={styles.scanText}>
            {showBonus ? (
              <>
                <p className={styles.scanLabel}>Scan to play</p>
                <p className={styles.scanOffer} style={{ fontSize: offerFontSize(bonusCta) }}>
                  {bonusCta}
                </p>
              </>
            ) : (
              <>
                <p className={styles.scanLabel}>Start here</p>
                <p className={styles.scanHeadline}>Scan to play</p>
              </>
            )}
            <ol className={styles.steps}>
              {steps.map((step, index) => (
                <li key={step} className={styles.step}>
                  <span className={styles.stepNumber}>{index + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <div className={styles.urlRow}>
          <span className={styles.urlLabel}>Or go to</span>
          <span className={styles.url}>{readableUrl(kioskUrl)}</span>
        </div>
      </div>
    </div>
  );
});

export default ShopPromoPoster;
